import {
  Divider,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Button,
  FormHelperText,
} from "@mui/material";
import React, { Component } from "react";
import TerminalCommandInput, {
  InputType,
  TerminalCommandInputType,
} from "./TerminalCommandInput";
import * as api from "../../../repo/api";
import TerminalCommandEntry from "./TerminalCommandEntry";
import { AxiosResponse } from "axios";

type myProps = {
  terminal: any;
  onError: (msg: string) => void;
};

type myState = {
  selectedCommand: Command;
  commandsForTID: HostTerminalCommand[];
  argumentValues: any;
  superUserMode: boolean;
};

export interface Command {
  commandName: string;
  enabled: boolean;
  parameters: TerminalCommandInputType[];
  description?: string;
}

export interface HostTerminalCommand {
  _id: string;
  TID: string;
  commandType: string;
  createdAt: Date;
  updatedAt: Date;
  host: string;
  parameters: TerminalCommandInputType[];
  sent: boolean;
  response?: string;
  responseTimestamp?: Date;
}

class TerminalCommands extends Component<myProps, myState> {
  tid: string;

  commands: Command[] = [
    {
      commandName: "REESTABLISH_WEBSOCKET",
      description:
        "Will restart the websocket connection on the terminal. This is useful for redirecting traffic to a specific prod host.",
      enabled: true,
      parameters: [
        {
          key: "wait_until_idle",
          label: "Wait until idle",
          inputType: "CHECKBOX" as InputType,
          required: true,
          default: true,
        },
      ],
    },
    {
      commandName: "GET_LOGS",
      enabled: false,
      parameters: [],
    },
    {
      commandName: "GET_APN",
      enabled: true,
      parameters: [
        {
          key: "sim",
          inputType: "DROPDOWN" as InputType,
          inputOptions: ["SIM1", "SIM2"],
          label: "Sim for APN",
          required: true,
        },
      ],
    },
    {
      commandName: "SET_APN",
      enabled: false,
      parameters: [],
    },
    {
      commandName: "GET_CURRENT_WIFI",
      enabled: true,
      parameters: [],
    },
    {
      commandName: "REFRESH_TERMINAL_CONFIG",
      description:
        "Gets the terminal to request it's terminal config again. This is the equivalent of pressing (i) on the terminal. This will only work when the terminal is idle.",
      enabled: true,
      parameters: [],
    },
    {
      commandName: "RESTART_SATELLITE",
      description:
        "Gets the satellite app to restart (just the app, not the power).",
      enabled: true,
      parameters: [
        {
          key: "wait_until_idle",
          label: "Wait until idle",
          inputType: "CHECKBOX" as InputType,
          required: true,
          default: false,
        },
      ],
    },
    {
      commandName: "RESTART_TERMINAL",
      description: "Gets the terminal to do a power restart.",
      enabled: true,
      parameters: [
        {
          key: "wait_until_idle",
          label: "Wait until idle",
          inputType: "CHECKBOX" as InputType,
          required: true,
          default: true,
        },
      ],
    },
    {
      commandName: "DOWNLOAD_FILE",
      description:
        "Get's the terminal to download a file and store it to /assets/",
      enabled: false,
      parameters: [
        {
          key: "filename",
          inputType: "TEXT" as InputType,
          label: "Filename",
          required: true,
        },
      ],
    },
    {
      commandName: "DELETE_FILE",
      enabled: true,
      parameters: [
        {
          key: "directory",
          inputType: "DROPDOWN" as InputType,
          inputOptions: ["Downloads", "Local"],
          label: "Directory",
          required: true,
        },
        {
          key: "filename",
          inputType: "TEXT" as InputType,
          label: "Filename",
          required: true,
        },
      ],
    },
    {
      commandName: "LIST_FILES",
      enabled: true,
      parameters: [
        {
          key: "directory",
          inputType: "DROPDOWN" as InputType,
          inputOptions: ["Downloads", "Local"],
          label: "Directory",
          required: true,
        },
      ],
    },
    {
      commandName: "INSTALL_APK",
      enabled: false,
      parameters: [
        {
          key: "filename",
          inputType: "TEXT" as InputType,
          label: "Filename",
          required: true,
        },
      ],
    },
    {
      commandName: "SHOW_TOAST",
      description: "Show a toast animation on the terminal.",
      enabled: true,
      parameters: [
        {
          key: "message",
          inputType: "TEXT" as InputType,
          label: "Message",
          required: true,
        },
      ],
    },
    {
      commandName: "SHOW_PROMPT",
      description:
        "Show's a prompt on the terminal with a title and message. There will be an OK button on the terminal and the dialog willl remain until OK is pressed.",
      enabled: true,
      parameters: [
        {
          key: "title",
          inputType: "TEXT" as InputType,
          label: "Title",
          required: true,
        },
        {
          key: "message",
          inputType: "TEXT" as InputType,
          label: "Message",
          required: true,
        },
      ],
    },
    {
      commandName: "GET_FIELD",
      description: "Retrieve information from the terminal.",
      enabled: true,
      parameters: [
        {
          key: "field",
          inputType: "DROPDOWN" as InputType,
          inputOptions: [
            "APP_VERSION",
            "NETWORK_CONNECTION_MANAGER_ENABLED",
            "FIRMWARE_VERSION",
            "CURRENT_TIMESTAMP",
            "CURRENT_SCREEN",
          ],
          label: "Field",
          required: true,
        },
      ],
    },
    {
      commandName: "UPDATE_TERMINAL",
      description: "Start the update procedure on the terminal",
      enabled: true,
      parameters: [
        {
          key: "wait_for_idle",
          inputType: "CHECKBOX" as InputType,
          label: "Wait For Idle",
          required: true,
          default: true,
        },
        {
          key: "silent",
          inputType: "CHECKBOX" as InputType,
          label: "Update without user input (no prompts)",
          required: true,
          default: true,
        },
        {
          key: "use_legacy",
          inputType: ("CHECKBOX" as InputType) as InputType,
          label: "Use Legacy Update method (HostURL field)",
          required: true,
          default: true,
        },
      ],
    },
  ].filter((s: any) => {
    return s?.enabled ?? false;
  });

  async updateAllTerminalCommands() {
    let res;
    try {
      res = await api.getTerminalCommandsForTID(this.tid);
    } catch (e) {
      this.props.onError(JSON.stringify(e));
      return;
    }
    console.log(res.data);
    if (Array.isArray(res.data)) {
      this.setState({
        commandsForTID: res.data,
      });
    }
  }

  updateArgumentValue(key: string, value: boolean | string) {
    console.log("Got update value " + value);

    if (value === "godmode") {
      this.setState({
        superUserMode: true,
      });
    }

    if (this.state.argumentValues.key === value) {
      // no need to do anything, value already set.
      return;
    }

    let copy: any;
    if (this.state.argumentValues === undefined) {
      copy = {};
    } else {
      copy = Object.assign({}, this.state.argumentValues);
    }
    copy[key] = value;
    this.setState({
      argumentValues: copy,
    });
  }

  async deleteCommand(id: string) {
    let res;
    try {
      res = await api.deleteTerminalCommandById(id);
    } catch (e) {
      this.props.onError(JSON.stringify(e));
      return;
    }

    console.log(this.state.commandsForTID);
    console.log(
      this.state.commandsForTID.filter((x) => {
        x._id != id;
      })
    );

    this.setState({
      commandsForTID: this.state.commandsForTID.filter((x) => x._id != id),
    });
  }

  async createTerminalCommand(every: boolean) {
    let params: any = {};
    let failed = false;
    this.state.selectedCommand.parameters.forEach((arg) => {
      // neccessary check if the user is relying on the default value, as in the checkbox is checked and the setter hasn't
      // executed.
      if (
        this.state.argumentValues[arg.key] === undefined &&
        arg.default !== undefined
      ) {
        if (arg.default !== "") {
          this.setState(prevState => ({
            argumentValues: {
              ...prevState.argumentValues,
              [arg.key]: arg.default
            }
          }));
        }
      }

      // check if the argument is required and we haven't provided a value.
      if (arg.required && this.state.argumentValues[arg.key] === undefined) {
        failed = true;
        console.log("Missing argument with key " + arg.key);
        alert("Missing argument " + arg.label);
        return;
      }

      params[arg.key] = this.state.argumentValues[arg.key];
    });

    if (failed) {
      return;
    }

    let res: AxiosResponse<any> | undefined = undefined;
    try {
      if (every) {
        res = await api.postCreateTerminalCommandForAll({
          type: this.state.selectedCommand.commandName,
          parameters: params,
        });
      } else {
        res = await api.postCreateTerminalCommand(this.tid, {
          type: this.state.selectedCommand.commandName,
          parameters: params,
        });
      }
    } catch (e) {
      alert(e);
    }
    console.log(res);
    if (res && res.status === 200 && res.data._id !== undefined) {
      this.setState({
        commandsForTID: [...this.state.commandsForTID, res.data],
      });
    }
  }

  async refreshTerminalCommand(id: string) {
    let res = await api.getTerminalCommandById(id);
    if (res.status === 200) {
      this.setState({
        commandsForTID: this.state.commandsForTID.map((x) => {
          if (x._id == res.data._id) {
            return res.data;
          }
          return x;
        }),
      });
    } else {
      console.log("Error making request " + JSON.stringify(res));
    }
  }

  componentDidMount() {
    this.updateAllTerminalCommands();
  }

  isSubmitEnabled(): boolean {
    if (this.state.selectedCommand.enabled) {
      return true;
    }
    return false;
  }

  onSubmitButtonClick() {
    this.createTerminalCommand(false);
  }

  onSubmitButtonForAllTerminalsClick() {
    this.createTerminalCommand(true);
  }

  constructor(props: any) {
    super(props);
    this.tid = props.terminal.attributes.TID;
    this.state = {
      superUserMode: false,
      selectedCommand: {
        commandName: "",
        enabled: false,
        parameters: [],
      },
      commandsForTID: [],
      argumentValues: {},
    };
  }

  onChangedTerminalCommand = (event: any): void => {
    let command = this.commands.find(
      (x) => x.commandName === event.target.value
    ) || {
      commandName: "",
      enabled: false,
      parameters: [],
      argumentValues: {},
    };

    const newArgs: any = {};
    command.parameters
      .filter((x) => x.default !== undefined)
      .forEach((arg) => {
        newArgs[arg.key] = arg.default;
      });

    console.log("DEBUG 5", "setting state", command, newArgs);

    this.setState({
      selectedCommand: {
        commandName: "",
        enabled: false,
        parameters: [],
      },
      argumentValues: {},
    });

    console.log("DEBUG 6", "setting state", command, newArgs);

    this.setState({
      selectedCommand: command,
      argumentValues: newArgs,
    });
  };

  onRefreshPressed(id: string) {
    console.log("refresh pressed " + id);
    this.refreshTerminalCommand(id);
  }

  onDeletePressed(id: string) {
    console.log("delete pressed " + id);
    this.deleteCommand(id);
  }

  render = (): JSX.Element => {
    return (
      <div>
        <Typography variant={"h4"}>
          Active commands for Terminal {this.tid}
        </Typography>
        <Divider />
        <br />
        {this.state.commandsForTID.map((hostCommand) => {
          return (
            <TerminalCommandEntry
              key={hostCommand._id}
              command={hostCommand}
              onRefreshPressed={(() => {
                this.onRefreshPressed(hostCommand._id);
              }).bind(this)}
              onDeletePressed={(() => {
                this.onDeletePressed(hostCommand._id);
              }).bind(this)}
            />
          );
        })}

        <br />
        <Typography variant={"h4"}>Create a terminal Command</Typography>
        <FormControl variant="outlined" style={{ minWidth: "300px" }}>
          <InputLabel id="select-terminal-command-label">
            Terminal Command
          </InputLabel>
          <Select
            labelId="select-terminal-command-label"
            id="terminal-command-select"
            value={this.state.selectedCommand.commandName}
            label="Terminal Command"
            onChange={this.onChangedTerminalCommand}
          >
            {this.commands.map((command) => {
              return (
                <MenuItem key={command.commandName} value={command.commandName}>
                  {command.commandName}
                </MenuItem>
              );
            })}
          </Select>
          <FormHelperText>
            {this.state.selectedCommand.commandName === "" ||
            this.state.selectedCommand.enabled
              ? ""
              : "Currently not implemented."}
          </FormHelperText>
        </FormControl>
        <Divider />
        <br />
        {this.state.selectedCommand.description && (
          <Typography variant={"h6"}>
            {this.state.selectedCommand.description}
          </Typography>
        )}
        {this.state.selectedCommand.parameters.length > 0 && (
          <>
            <Typography variant={"h5"}>Please specify arguments</Typography>
            {this.state.selectedCommand.parameters.map(
              (arg: TerminalCommandInputType) => {
                console.log("DEBUG2", arg);
                return (
                  <TerminalCommandInput
                    key={arg.label}
                    input={arg}
                    value={
                      this.state.argumentValues[arg.key] ?? arg.default ?? ""
                    }
                    onUpdate={this.updateArgumentValue.bind(this)}
                  />
                );
              }
            )}
          </>
        )}

        <Divider />
        <br />
        <br />

        <Typography>{JSON.stringify(this.state.argumentValues)}</Typography>

        <Button
          variant="contained"
          color="primary"
          disabled={!this.isSubmitEnabled()}
          onClick={this.onSubmitButtonClick.bind(this)}
        >
          Send Command
        </Button>

        {this.state.superUserMode && (
          <>
            <br />
            <Button
              variant="contained"
              color="primary"
              disabled={!this.isSubmitEnabled()}
              onClick={this.onSubmitButtonForAllTerminalsClick.bind(this)}
            >
              Send Command To All Terminals (ARE YOU SURE???)
            </Button>
          </>
        )}
      </div>
    );
  };
}

export default TerminalCommands;
